import React from 'react';
import styled from 'styled-components';
import { theme } from '../../../styles/theme';

export interface DateOfBirthSelectorProps {
  /** Current date of birth, or undefined/null if not yet set. */
  value?: Date | null;
  /** Fires with a Date built from local year/month/day — never parsed from a string, so there's no UTC/local timezone shift. */
  onChange: (date: Date) => void;
  onBlur?: () => void;
  hasError?: boolean;
  disabled?: boolean;
  /** Youngest allowed age (default 18). */
  minAge?: number;
  /** Oldest allowed age (default 100). */
  maxAge?: number;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

function daysInMonth(year: number, monthIndex: number): number {
  // Day 0 of the following month is the last day of this one — leap-year safe.
  return new Date(year, monthIndex + 1, 0).getDate();
}

export const DateOfBirthSelector: React.FC<DateOfBirthSelectorProps> = ({
  value,
  onChange,
  onBlur,
  hasError,
  disabled,
  minAge = 18,
  maxAge = 100,
}) => {
  const currentYear = new Date().getFullYear();
  const maxYear = currentYear - minAge;
  const minYear = currentYear - maxAge;

  const selectedYear = value ? value.getFullYear() : undefined;
  const selectedMonth = value ? value.getMonth() : undefined; // 0-indexed
  const selectedDay = value ? value.getDate() : undefined;

  const dayCount = selectedYear !== undefined && selectedMonth !== undefined
    ? daysInMonth(selectedYear, selectedMonth)
    : 31;

  const emit = (year: number, month: number, day: number) => {
    const clampedDay = Math.min(day, daysInMonth(year, month));
    onChange(new Date(year, month, clampedDay));
  };

  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    emit(selectedYear ?? maxYear, parseInt(e.target.value, 10), selectedDay ?? 1);
  };

  const handleDayChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    emit(selectedYear ?? maxYear, selectedMonth ?? 0, parseInt(e.target.value, 10));
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    emit(parseInt(e.target.value, 10), selectedMonth ?? 0, selectedDay ?? 1);
  };

  const years: number[] = [];
  for (let y = maxYear; y >= minYear; y--) years.push(y);

  const days: number[] = [];
  for (let d = 1; d <= dayCount; d++) days.push(d);

  return (
    <DOBRow>
      <DOBSelect aria-label="Month of birth" value={selectedMonth ?? ''} onChange={handleMonthChange} onBlur={onBlur} $hasError={hasError} disabled={disabled}>
        <option value="" disabled>Month</option>
        {MONTH_NAMES.map((name, index) => (
          <option key={name} value={index}>{name}</option>
        ))}
      </DOBSelect>

      <DOBSelect aria-label="Day of birth" value={selectedDay ?? ''} onChange={handleDayChange} onBlur={onBlur} $hasError={hasError} disabled={disabled}>
        <option value="" disabled>Day</option>
        {days.map((day) => (
          <option key={day} value={day}>{day}</option>
        ))}
      </DOBSelect>

      <DOBSelect aria-label="Year of birth" value={selectedYear ?? ''} onChange={handleYearChange} onBlur={onBlur} $hasError={hasError} disabled={disabled}>
        <option value="" disabled>Year</option>
        {years.map((year) => (
          <option key={year} value={year}>{year}</option>
        ))}
      </DOBSelect>
    </DOBRow>
  );
};

const DOBRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: ${theme.spacing.sm};
`;

const DOBSelect = styled.select<{ $hasError?: boolean }>`
  width: 100%;
  padding: 0.75rem;
  background: ${theme.colors.background.card};
  border: 1px solid ${props => props.$hasError ? theme.colors.error : theme.colors.border.medium};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.text.primary};
  font-size: 0.9rem;

  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? theme.colors.error : theme.colors.primary};
    box-shadow: 0 0 0 2px ${props => props.$hasError ? `${theme.colors.error}20` : `${theme.colors.primary}20`};
  }

  option {
    background: ${theme.colors.background.card};
    color: ${theme.colors.text.primary};
  }
`;
